// src/pages/Admin/VotePage/components/VotesMobileList.jsx

import React from "react";

import {
  FaCalendarAlt,
  FaChartPie,
  FaEdit,
  FaListUl,
  FaQuestionCircle,
  FaTrash,
  FaUsers,
} from "react-icons/fa";

import AudienceChips from "../ui/AudienceChips";
import VoteActionButton from "../ui/VoteActionButton";
import VoteStatusPill from "../ui/VoteStatusPill";
import VoteTypePill from "../ui/VoteTypePill";
import VoteClubRulesPills from "../ui/VoteClubRulesPills";

import { formatVotePeriod } from "../utils/voteAdmin.utils";

function MobileSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="h-72 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
        />
      ))}
    </div>
  );
}

export default function VotesMobileList({
  votes,
  loading,
  deletingId,
  actorGroups,
  onEdit,
  onQuestions,
  onResults,
  onDelete,
}) {
  if (loading) {
    return (
      <section className="lg:hidden">
        <MobileSkeleton />
      </section>
    );
  }

  return (
    <section className="space-y-3 lg:hidden">
      {votes.map((vote) => (
        <article
          key={vote.id}
          className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="h-1 bg-gradient-to-r from-indigo-600 via-violet-500 to-fuchsia-500" />

          <div className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <VoteStatusPill status={vote._status} />

              <VoteTypePill type={vote.type} isAnonymous={vote.is_anonymous} />
            </div>

            <h2 className="mt-4 text-base font-black leading-snug text-slate-950 dark:text-white">
              {vote.title}
            </h2>

            {vote.description && (
              <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                {vote.description}
              </p>
            )}

            <VoteClubRulesPills vote={vote} className="mt-3" />

            <div className="mt-4 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/60">
              <div className="flex items-center gap-2 text-xs font-black text-slate-500 dark:text-slate-300">
                <FaCalendarAlt className="text-indigo-500" />
                {formatVotePeriod(vote)}
              </div>
            </div>

            <div className="mt-4">
              <div className="mb-2 text-[10px] font-black uppercase tracking-[0.13em] text-slate-400">
                Audience
              </div>

              <AudienceChips
                roleIds={vote._allowedRoleIds}
                actorGroupIds={vote._allowedActorGroupIds}
                actorGroups={actorGroups}
                selectedActorGroups={vote.allowed_actor_groups ?? []}
              />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 dark:border-indigo-500/20 dark:bg-indigo-500/10">
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                  <FaQuestionCircle />
                  Questions
                </div>

                <div className="mt-1 text-xl font-black text-slate-950 dark:text-white">
                  {vote.questionCount ?? 0}
                </div>
              </div>

              <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 dark:border-emerald-500/20 dark:bg-emerald-500/10">
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
                  <FaUsers />
                  Votants
                </div>

                <div className="mt-1 text-xl font-black text-slate-950 dark:text-white">
                  {vote.voterCount ?? 0}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <VoteActionButton
                icon={FaEdit}
                label="Modifier"
                onClick={() => onEdit(vote)}
              />

              <VoteActionButton
                icon={FaListUl}
                label="Questions"
                variant="indigo"
                onClick={() => onQuestions(vote)}
              />

              <VoteActionButton
                icon={FaChartPie}
                label="Résultats"
                variant="violet"
                onClick={() => onResults(vote)}
              />

              <VoteActionButton
                icon={FaTrash}
                label="Supprimer"
                variant="danger"
                disabled={deletingId === vote.id}
                onClick={() => onDelete(vote)}
              />
            </div>
          </div>
        </article>
      ))}

      {!votes.length && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm font-bold text-slate-500 dark:border-slate-700 dark:bg-slate-900">
          Aucun vote ne correspond à votre recherche.
        </div>
      )}
    </section>
  );
}
